import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { RequirePermission } from "../../components/crm/RequirePermission";
import {
  SupportChannelLabel,
  SupportOrientationBadge,
  SupportPriorityBadge,
  SupportSlaKindLabel,
  SupportSlaStatusBadge,
  SupportStatusBadge,
} from "../../components/support/SupportBadges";
import { Spinner } from "../../components/Spinner";
import { StepUpDialog } from "../../components/StepUpDialog";
import { ApiError, mfaChallenge } from "../../lib/api";
import { formatDate } from "../../lib/format";
import type { TranslationKey } from "../../lib/i18n";
import {
  addReply,
  assignTicket,
  closeTicket,
  getTicket,
  isApiError,
  reopenTicket,
  resolveTicket,
  supportErrorCode,
} from "../../lib/support-api";
import type { ReplyOrientation, TicketDetail } from "../../lib/support-types";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

const ERROR_KEYS: Record<string, TranslationKey> = {
  support_duplicate: "support.error.duplicate",
  invalid_state_transition: "support.error.invalidTransition",
  reopen_not_allowed: "support.error.reopenNotAllowed",
  reopen_limit_reached: "support.error.reopenLimit",
  reopen_reason_required: "support.error.reopenReasonRequired",
  support_ticket_not_found: "support.error.notFound",
};

type ActionPanel = "assign" | "resolve" | "close" | "reopen" | null;

function Field({ label, children }: { label: string; children: ReactNode }): ReactNode {
  return (
    <div className="detail-list__row">
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

function SupportTicketDetailInner({ id }: { id: string }): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canReply = hasPermission("support.ticket.reply");
  const canAssign = hasPermission("support.ticket.assign");
  const canClose = hasPermission("support.ticket.close");
  const canUpdate = hasPermission("support.ticket.update");

  const [detail, setDetail] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [panel, setPanel] = useState<ActionPanel>(null);
  const [busy, setBusy] = useState(false);
  const [assigneeInput, setAssigneeInput] = useState("");
  const [reason, setReason] = useState("");
  const [replyBody, setReplyBody] = useState("");
  const [replyOrientation, setReplyOrientation] = useState<ReplyOrientation>("outbound");
  const [replyBusy, setReplyBusy] = useState(false);
  const [pending, setPending] = useState<(() => Promise<void>) | null>(null);
  const [challengeBusy, setChallengeBusy] = useState(false);
  const [challengeError, setChallengeError] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await getTicket(id));
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const runAction = useCallback(
    async (action: () => Promise<void>): Promise<void> => {
      setActionError(null);
      setBusy(true);
      try {
        await action();
        setPanel(null);
        setReason("");
        setAssigneeInput("");
        await load();
      } catch (caught) {
        if (caught instanceof ApiError && caught.code === "mfa_required") {
          setChallengeError(null);
          setPending(() => action);
          return;
        }
        const code = supportErrorCode(caught);
        const key = code === null ? undefined : ERROR_KEYS[code];
        setActionError(
          key !== undefined
            ? t(key)
            : isApiError(caught)
              ? caught.detail
              : t("error.network"),
        );
      } finally {
        setBusy(false);
      }
    },
    [load, t],
  );

  const onChallengeSubmit = async (code: string): Promise<void> => {
    setChallengeBusy(true);
    setChallengeError(null);
    try {
      await mfaChallenge(code);
      const action = pending;
      setPending(null);
      if (action !== null) {
        await runAction(action);
      }
    } catch (caught) {
      setChallengeError(caught instanceof ApiError ? caught.detail : t("error.network"));
    } finally {
      setChallengeBusy(false);
    }
  };

  const onReply = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setReplyBusy(true);
    setActionError(null);
    try {
      await addReply(id, { body: replyBody.trim(), orientation: replyOrientation });
      setReplyBody("");
      await load();
    } catch (caught) {
      const code = supportErrorCode(caught);
      const key = code === null ? undefined : ERROR_KEYS[code];
      setActionError(
        key !== undefined ? t(key) : isApiError(caught) ? caught.detail : t("error.network"),
      );
    } finally {
      setReplyBusy(false);
    }
  };

  if (loading) {
    return <Spinner label={t("app.loading")} />;
  }
  if (detail === null) {
    return (
      <section className="page">
        <Alert tone="error">{error ?? t("support.error.notFound")}</Alert>
        <Link className="btn btn--ghost" to="/support/tickets">
          {t("support.tickets.title")}
        </Link>
      </section>
    );
  }

  const status = detail.status;
  const canResolve = canClose && (status === "new" || status === "open" || status === "pending");
  const canCloseAction = canClose && status !== "closed";
  const canReopen = canUpdate && (status === "resolved" || status === "closed");
  const reopenReasonRequired = detail.reopened_count > 2;

  return (
    <section className="page" aria-labelledby="support-ticket-detail-title">
      <header className="page__header page__header--row">
        <div>
          <p className="muted">
            <Link to="/support/tickets">{t("support.tickets.title")}</Link>
          </p>
          <h1 id="support-ticket-detail-title">{detail.subject}</h1>
          <p className="muted">
            <SupportStatusBadge status={detail.status} /> <SupportPriorityBadge priority={detail.priority} />
          </p>
        </div>
        <div className="inline-actions">
          {canAssign ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setPanel(panel === "assign" ? null : "assign")}
            >
              {t("support.action.assign")}
            </button>
          ) : null}
          {canResolve ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setPanel(panel === "resolve" ? null : "resolve")}
            >
              {t("support.action.resolve")}
            </button>
          ) : null}
          {canCloseAction ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setPanel(panel === "close" ? null : "close")}
            >
              {t("support.action.close")}
            </button>
          ) : null}
          {canReopen ? (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setPanel(panel === "reopen" ? null : "reopen")}
            >
              {t("support.action.reopen")}
            </button>
          ) : null}
        </div>
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}
      {actionError !== null ? <Alert tone="error">{actionError}</Alert> : null}

      {panel === "assign" ? (
        <form
          className="form form--inline-card"
          onSubmit={(event) => {
            event.preventDefault();
            void runAction(async () => {
              await assignTicket(id, {
                assignee_user_id: assigneeInput.trim() === "" ? null : assigneeInput.trim(),
                reason: reason.trim() === "" ? undefined : reason.trim(),
              });
            });
          }}
        >
          <h2 className="section-title">{t("support.action.assignTitle")}</h2>
          <div className="form-grid">
            <label className="field" htmlFor="assign-assignee">
              <span>{t("support.ticket.assignee")}</span>
              <input
                id="assign-assignee"
                value={assigneeInput}
                aria-describedby="assign-assignee-hint"
                onChange={(event) => setAssigneeInput(event.target.value)}
              />
              <small id="assign-assignee-hint" className="field__hint">
                {t("support.action.assignHint")}
              </small>
            </label>
            <label className="field" htmlFor="assign-reason">
              <span>{t("support.action.reason")}</span>
              <input
                id="assign-reason"
                value={reason}
                maxLength={500}
                onChange={(event) => setReason(event.target.value)}
              />
              <small className="field__hint">{t("support.action.reasonHint")}</small>
            </label>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {t("support.action.assign")}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setPanel(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      ) : null}

      {panel === "resolve" || panel === "close" || panel === "reopen" ? (
        <form
          className="form form--inline-card"
          onSubmit={(event) => {
            event.preventDefault();
            const trimmedReason = reason.trim();
            if (panel === "reopen" && reopenReasonRequired && trimmedReason === "") {
              setActionError(t("support.action.reasonRequired"));
              return;
            }
            void runAction(async () => {
              const payload = { reason: trimmedReason === "" ? undefined : trimmedReason };
              if (panel === "resolve") {
                await resolveTicket(id, payload);
              } else if (panel === "close") {
                await closeTicket(id, payload);
              } else {
                await reopenTicket(id, payload);
              }
            });
          }}
        >
          <h2 className="section-title">
            {panel === "resolve"
              ? t("support.action.resolveTitle")
              : panel === "close"
                ? t("support.action.closeTitle")
                : t("support.action.reopenTitle")}
          </h2>
          {panel === "reopen" ? (
            <p className="muted">{t("support.action.reopenMfaHint")}</p>
          ) : null}
          <label className="field" htmlFor="action-reason">
            <span>{t("support.action.reason")}</span>
            <input
              id="action-reason"
              value={reason}
              maxLength={500}
              required={panel === "reopen" && reopenReasonRequired}
              aria-describedby="action-reason-hint"
              onChange={(event) => setReason(event.target.value)}
            />
            <small id="action-reason-hint" className="field__hint">
              {panel === "reopen" && reopenReasonRequired
                ? t("support.action.reasonRequired")
                : t("support.action.reasonHint")}
            </small>
          </label>
          <div className="form-actions">
            <button type="submit" className="btn btn--primary" disabled={busy}>
              {panel === "resolve"
                ? t("support.action.resolve")
                : panel === "close"
                  ? t("support.action.close")
                  : t("support.action.reopen")}
            </button>
            <button type="button" className="btn btn--ghost" onClick={() => setPanel(null)}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      ) : null}

      <dl className="detail-list">
        <Field label={t("support.ticket.description")}>
          <span className="support-pre">{detail.description}</span>
        </Field>
        <Field label={t("support.ticket.category")}>
          {detail.category_id ?? t("support.ticket.categoryNone")}
        </Field>
        <Field label={t("support.ticket.requester")}>
          {[detail.requester_name, detail.requester_email].filter(Boolean).join(" · ") ||
            t("support.common.none")}
        </Field>
        <Field label={t("support.ticket.assignee")}>
          {detail.assignee_user_id ?? t("support.ticket.assigneeNone")}
        </Field>
        <Field label={t("support.ticket.channel")}>
          <SupportChannelLabel channel={detail.channel} />
        </Field>
        <Field label={t("support.ticket.created")}>
          {formatDate(detail.created_at, locale, true)}
        </Field>
        <Field label={t("support.ticket.updated")}>
          {formatDate(detail.updated_at, locale, true)}
        </Field>
        <Field label={t("support.ticket.firstResponse")}>
          {formatDate(detail.first_response_at, locale, true)}
        </Field>
        <Field label={t("support.ticket.resolvedAt")}>
          {formatDate(detail.resolved_at, locale, true)}
        </Field>
        <Field label={t("support.ticket.closedAt")}>
          {formatDate(detail.closed_at, locale, true)}
        </Field>
        <Field label={t("support.ticket.reopenedCount")}>{String(detail.reopened_count)}</Field>
      </dl>

      <h2 className="section-title">{t("support.slaEvents.title")}</h2>
      {detail.sla_events.length === 0 ? (
        <p className="empty">{t("support.slaEvents.empty")}</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("support.slaEvents.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("support.slaEvents.kind")}</th>
                <th scope="col">{t("support.slaEvents.status")}</th>
                <th scope="col">{t("support.slaEvents.policy")}</th>
                <th scope="col">{t("support.slaEvents.dueAt")}</th>
              </tr>
            </thead>
            <tbody>
              {detail.sla_events.map((event) => (
                <tr key={event.id}>
                  <td>
                    <SupportSlaKindLabel kind={event.kind} />
                  </td>
                  <td>
                    <SupportSlaStatusBadge status={event.status} />
                  </td>
                  <td>{event.policy_name ?? t("support.common.none")}</td>
                  <td>{formatDate(event.due_at, locale, true)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h2 className="section-title">{t("support.replies.title")}</h2>
      {detail.replies.length === 0 ? (
        <p className="empty">{t("support.replies.empty")}</p>
      ) : (
        <ol className="timeline">
          {detail.replies.map((reply) => (
            <li
              className={`timeline__item${reply.orientation === "internal" ? " timeline__item--internal" : ""}`}
              key={reply.id}
            >
              <div className="timeline__meta">
                <SupportOrientationBadge orientation={reply.orientation} />
                <span className="muted">
                  {reply.author_name ?? reply.author_kind} · {formatDate(reply.created_at, locale, true)}
                </span>
              </div>
              <p className="timeline__body support-pre">{reply.body}</p>
            </li>
          ))}
        </ol>
      )}

      {canReply ? (
        <form className="form form--inline-card" onSubmit={(event) => void onReply(event)}>
          <h2 className="section-title">{t("support.action.reply")}</h2>
          <div className="form-grid">
            <label className="field" htmlFor="reply-orientation">
              <span>{t("support.replies.orientation")}</span>
              <select
                id="reply-orientation"
                value={replyOrientation}
                onChange={(event) =>
                  setReplyOrientation(event.target.value as ReplyOrientation)
                }
              >
                <option value="outbound">{t("support.orientation.outbound")}</option>
                <option value="internal">{t("support.orientation.internal")}</option>
              </select>
            </label>
            <label className="field" htmlFor="reply-body">
              <span>{t("support.replies.composerLabel")}</span>
              <textarea
                id="reply-body"
                value={replyBody}
                rows={4}
                required
                aria-describedby="reply-body-hint"
                onChange={(event) => setReplyBody(event.target.value)}
              />
              <small id="reply-body-hint" className="field__hint">
                {replyOrientation === "internal"
                  ? t("support.replies.internalHint")
                  : t("support.action.reasonHint")}
              </small>
            </label>
          </div>
          <div className="form-actions">
            <button
              type="submit"
              className="btn btn--primary"
              disabled={replyBusy || replyBody.trim() === ""}
            >
              {replyBusy ? t("support.replies.sending") : t("support.replies.send")}
            </button>
          </div>
        </form>
      ) : null}

      <StepUpDialog
        open={pending !== null}
        busy={challengeBusy}
        error={challengeError}
        onCancel={() => {
          setPending(null);
          setChallengeError(null);
        }}
        onSubmit={(code) => void onChallengeSubmit(code)}
      />
    </section>
  );
}

function SupportTicketDetailRoute(): ReactNode {
  const { id } = useParams<{ id: string }>();
  const { t } = useConfig();
  if (id === undefined) {
    return (
      <section className="page">
        <Alert tone="error">{t("support.error.notFound")}</Alert>
        <Link className="btn btn--ghost" to="/support/tickets">
          {t("support.tickets.title")}
        </Link>
      </section>
    );
  }
  return <SupportTicketDetailInner id={id} />;
}

export function SupportTicketDetailPage(): ReactNode {
  return (
    <RequirePermission permission="support.ticket.read">
      <SupportTicketDetailRoute />
    </RequirePermission>
  );
}